import { getIn } from 'final-form';

import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';

import {
  ActOfficialsFormApi,
  buildInspectorFromIdentity,
  buildKnoInspectorOption,
  buildKnoInspectorRow,
  canMatchOfficial,
  changeFormField,
  getKnoInspectorIdentityIds,
  IOfficialIdentity,
  isOfficialUsedInActEvents,
  isSameOfficial,
  removeMatchingFieldArrayItems,
} from './actOfficialsSyncCommon';

const isSameKnoInspector = (item: IKnmErknmFormInspector, identity: IOfficialIdentity): boolean =>
  isSameOfficial(item.knoInspector?.value?.fullName, item.knoInspector?.value?.position, identity);

export const syncActKnoInspectorsFromSigner = ({
  formRef,
  passportInspectors,
  actFieldPrefix = 'organization.act',
  mode,
  original,
  updated,
}: {
  formRef: IDecisionFormRef | IKnmFormRef | undefined;
  passportInspectors: IKnmErknmFormInspector[] | undefined;
  actFieldPrefix?: string;
  mode: 'update' | 'remove';
  original: IOfficialIdentity;
  updated?: IOfficialIdentity;
}): void => {
  const form = formRef?.current as ActOfficialsFormApi | undefined;

  if (!form) {
    return;
  }

  const knoInspectorsField = `${actFieldPrefix}.knoInspectors`;
  const events = getIn(form.getState().values, `${actFieldPrefix}.events`) as IKnmErknmFormEvent[] | undefined;
  const knoInspectors = getIn(form.getState().values, knoInspectorsField) as IKnmErknmFormInspector[] | undefined;

  const isOriginalUsedInEvents =
    canMatchOfficial(original) &&
    isOfficialUsedInActEvents(events, original, getKnoInspectorIdentityIds(knoInspectors, original));

  const removeOriginalIfUnusedInEvents = (): void => {
    if (!canMatchOfficial(original) || isOriginalUsedInEvents) {
      return;
    }

    removeMatchingFieldArrayItems(form, knoInspectorsField, item =>
      isSameKnoInspector(item as IKnmErknmFormInspector, original),
    );
  };

  const addUpdatedIfMissing = (): void => {
    if (!updated || !canMatchOfficial(updated) || !form.mutators?.push) {
      return;
    }

    const currentKnoInspectors = getIn(form.getState().values, knoInspectorsField) as
      | IKnmErknmFormInspector[]
      | undefined;
    const isAlreadyExists = currentKnoInspectors?.some(item => isSameKnoInspector(item, updated));

    if (isAlreadyExists) {
      return;
    }

    form.mutators.push(knoInspectorsField, buildKnoInspectorRow(updated, passportInspectors));
  };

  if (mode === 'remove') {
    removeOriginalIfUnusedInEvents();

    return;
  }

  if (canMatchOfficial(original) && (!updated || !canMatchOfficial(updated))) {
    removeOriginalIfUnusedInEvents();

    return;
  }

  if (!updated || !canMatchOfficial(updated)) {
    return;
  }

  if (canMatchOfficial(original) && !isSameOfficial(updated.fullName, updated.position, original)) {
    const isUpdatedAlreadyExists = knoInspectors?.some(item => isSameKnoInspector(item, updated));

    // Если original ещё нужен в перечне действий — не заменяем и не удаляем его, только добавляем updated.
    if (isOriginalUsedInEvents) {
      addUpdatedIfMissing();

      return;
    }

    // Если целевая пара ФИО+должность уже есть — не превращаем original в updated (иначе получим дубль).
    // Просто убираем записи original.
    if (isUpdatedAlreadyExists) {
      removeOriginalIfUnusedInEvents();

      return;
    }

    knoInspectors?.forEach((item, index) => {
      if (!isSameKnoInspector(item, original)) {
        return;
      }

      const nextInspectorValue = buildInspectorFromIdentity(updated, passportInspectors, item.knoInspector?.value);

      changeFormField(
        form,
        `${knoInspectorsField}[${index}].knoInspector`,
        buildKnoInspectorOption(nextInspectorValue, item.knoInspector),
      );
    });

    return;
  }

  addUpdatedIfMissing();
};
